<template>
  <div class="quill-cdn-wrapper">
    <div v-if="loadFailed === false" ref="editorEl" class="quill-editor"></div>
    <textarea
      v-if="loadFailed === true"
      class="quill-fallback"
      :value="modelValue"
      :placeholder="placeholder"
      :dir="resolveDirection(modelValue)"
      @input="handleFallbackInput"
    ></textarea>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue';
import { marked } from 'marked';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { useI18n } from '@/i18n';

const props = defineProps({
  modelValue: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  direction: { type: String, default: 'auto' }
});
const emit = defineEmits(['update:modelValue']);
const { currentLanguage } = useI18n();

const editorEl = ref(null);
let quillInstance = null;
const internalSync = ref(false);
const loadFailed = ref(false);

function markdownToHtml(md) {
  if (typeof md !== 'string') return '';
  const replaced = md.replace(/==([^=]+)==/g, '<span style="background-color:#fff3a1">$1</span>');
  return marked.parse(replaced);
}

function sanitizeHtml(rawHtml) {
  const parser = new DOMParser();
  const doc = parser.parseFromString('<div>' + rawHtml + '</div>', 'text/html');
  const container = doc.body.firstChild;
  if (container === undefined || container === null) return '';
  const allowed = new Set(['div', 'span', 'h1', 'h2', 'h3', 'p', 'br', 'strong', 'b', 'em', 'i', 'a', 'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'hr']);
  function clean(node) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = node.tagName.toLowerCase();
      if (allowed.has(tag) === false) {
        const parent = node.parentNode;
        while (node.firstChild !== null) parent.insertBefore(node.firstChild, node);
        parent.removeChild(node);
        return;
      }
      if (tag === 'a') {
        const href = node.getAttribute('href') !== undefined && node.getAttribute('href') !== null ? node.getAttribute('href') : '';
        if (/^(https?:|mailto:|#)/i.test(href) === false) {
          node.removeAttribute('href');
        } else {
          node.setAttribute('target', '_blank');
          node.setAttribute('rel', 'noopener noreferrer');
        }
      }
      if (node.hasAttribute('style') === true) {
        const style = node.getAttribute('style') || '';
        const parts = style.split(';').map(function(p){ return p.trim(); }).filter(function(p){ return p.length > 0; });
        const safeParts = parts.filter(function(p){ const l = p.toLowerCase(); return l.indexOf('background-color') === 0 || l.indexOf('color') === 0; });
        if (safeParts.length > 0) {
          node.setAttribute('style', safeParts.join(';'));
        } else {
          node.removeAttribute('style');
        }
      }
      Array.from(node.attributes).forEach(function(attr){ if (/^on/i.test(attr.name) === true) { node.removeAttribute(attr.name); } });
    }
    let child = node.firstChild;
    while (child !== null) {
      const next = child.nextSibling;
      clean(child);
      child = next;
    }
  }
  clean(container);
  return container.innerHTML.trim();
}

function convertModelToHtml(value) {
  if (value === undefined || value === null || value === '') return '';
  const trimmed = value.trim();
  if (/<[a-z][\s\S]*>/i.test(trimmed) === true) {
    return sanitizeHtml(trimmed);
  }
  return sanitizeHtml(markdownToHtml(trimmed));
}

function setEditorHtml(html) {
  if (quillInstance === null) return;
  if (editorEl.value === null) return;
  const root = editorEl.value.querySelector('.ql-editor');
  if (root !== null && root.innerHTML !== html) {
    quillInstance.clipboard.dangerouslyPasteHTML(html);
  }
}

function handleFallbackInput(event) {
  const nextValue = event !== undefined && event !== null && event.target !== undefined && event.target !== null && typeof event.target.value === 'string' ? event.target.value : '';
  emit('update:modelValue', nextValue);
}

onMounted(() => {
  if (editorEl.value !== null) {
    quillInstance = new Quill(editorEl.value, {
      theme: 'snow',
      placeholder: props.placeholder,
      modules: {
        toolbar: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic'],
          [{ background: [] }],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ direction: 'rtl' }],
          [{ align: [] }],
          ['blockquote', 'code-block'],
          ['link'],
          ['clean']
        ]
      },
      formats: ['header','bold','italic','background','list','blockquote','code-block','link','direction','align']
    });
    const initialHtml = convertModelToHtml(props.modelValue);
    if (initialHtml !== '') setEditorHtml(initialHtml);
    applyDirection(resolveDirection(initialHtml));
    quillInstance.on('text-change', function() {
      if (editorEl.value === null) return;
      const root = editorEl.value.querySelector('.ql-editor');
      const html = root !== null ? root.innerHTML : '';
      const safe = sanitizeHtml(html);
      internalSync.value = true;
      emit('update:modelValue', safe);
      internalSync.value = false;
      applyDirection(resolveDirection(safe));
    });
  }
});

watch(
  () => props.modelValue,
  (value) => {
    if (loadFailed.value === true) return;
    if (internalSync.value === true) return;
    const html = convertModelToHtml(value !== undefined && value !== null ? value : '');
    setEditorHtml(html);
    applyDirection(resolveDirection(html));
  },
  { immediate: false }
);

function resolveDirection(html) {
  if (props.direction === 'rtl') return 'rtl';
  if (props.direction === 'ltr') return 'ltr';
  const lang = (currentLanguage !== undefined && currentLanguage !== null && currentLanguage.value !== undefined) ? String(currentLanguage.value).toLowerCase() : '';
  if (lang === 'he' || lang === 'ar') return 'rtl';
  return 'ltr';
}

function applyDirection(dir) {
  const wrapper = editorEl.value;
  if (wrapper !== null) {
    wrapper.setAttribute('dir', dir);
  }
  const root = wrapper !== null ? wrapper.querySelector('.ql-editor') : null;
  if (root !== null) {
    root.setAttribute('dir', dir);
    root.style.textAlign = dir === 'rtl' ? 'right' : 'left';
  }
}

watch(() => (currentLanguage !== undefined && currentLanguage !== null ? currentLanguage.value : ''), () => {
  applyDirection(resolveDirection(''));
});
</script>

<style scoped>
.quill-cdn-wrapper {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background-color: #ffffff;
}
.quill-editor {
  min-height: 320px;
}
.quill-fallback {
  min-height: 320px;
  width: 100%;
  border: 0;
  border-radius: 12px;
  padding: 0.75rem 0.875rem;
  resize: vertical;
  background-color: #ffffff;
  color: #111827;
}
.quill-fallback:focus {
  outline: none;
}
.quill-cdn-wrapper :deep(.ql-toolbar) {
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  border-color: #e5e7eb;
}
.quill-cdn-wrapper :deep(.ql-container) {
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
  border-color: #e5e7eb;
}
</style>


